import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToastResultHandling } from '#layers/ui/public';
import { useServiceAccountDialogs } from './use-service-account-dialogs';

interface ServiceAccountCreationOptions {
	/** Applies the created account to whatever selection state the caller owns. */
	select: (serviceAccount: ResolvedMonospaceServiceAccount) => void;
	/**
	 * The caller's `useServiceAccountOptions` refresh. Creating an account does not invalidate that
	 * query, so the new account only appears once the caller refreshes it.
	 */
	refresh: () => unknown;
	prefillWorkspaces?: () => MonospaceWorkspace[];
}

/**
 * Drives the "create a service account" affordance a picker offers: opens the create dialog, then
 * selects and reveals the account it reports.
 */
export function useServiceAccountCreation({ select, refresh, prefillWorkspaces }: ServiceAccountCreationOptions) {
	const { t } = useI18n();
	const { create } = useServiceAccountDialogs();

	const { onError } = useToastResultHandling({
		error: {
			title: t('service_accounts.create_error'),
		},
	});

	const isCreating = ref(false);

	/**
	 * Select before refreshing: the account becomes the `selectedId` that `useServiceAccountOptions`
	 * hydrates on its own, so the selection holds while the first result page is still catching up,
	 * and survives an account that sorts past that page.
	 */
	async function createServiceAccount() {
		if (isCreating.value) return;

		isCreating.value = true;

		try {
			// The dialog reports the account it created; a listing diff could not recover one that
			// sorts outside the first server page.
			const created = await create({ prefillWorkspaces: prefillWorkspaces?.() });
			if (created) select(created);
			await refresh();
		}
		catch (error) {
			onError(error);
		}
		finally {
			isCreating.value = false;
		}
	}

	return { isCreating, createServiceAccount };
}
